import React, { useState, useEffect, useCallback } from 'react';
import { wwwRoot, vasRoot, hRoot, passportRoot, lptRoot, isCn, userUtil, cookieUtil, ajax } from '@liepin/js-core4c-fe';
import { Dropdown, message as Message } from 'antd';
import classnames from 'classnames';
import { useLocation } from 'react-router-dom';
import { requireLoginModal } from '@liepin/react-login4c-pc';
import eventBus from '@liepin/js-eventbus-fe';
import { LOGIN_SUCCESS } from '@liepin/js-eventbus-fe/enum';
import CityAddressSVG from '@liepin/icon-c-fe/svg/header-city-address.svg';
import CityArrowSVG from '@liepin/icon-c-fe/svg/header-city-arrow.svg';
import './index.less';
import { IUserInfo } from '../../types';
import CampusSubMenu from '../components/CampusSubMenu';
import ResumeOptimizationSubMenu from '../components/ResumeOptimizationSubMenu';
import QuickMenuSubMenu from '../components/QuickMenuSubMenu';
import QuickMenuSubMenuGray from '../components/QuickMenuSubMenuGray';
import { loadUserInfoFn } from '../../modules';
import { useSearchJob } from '../../hooks';
import { isWWW, isCityRouter, isCityZhaoPin } from '../../modules/const';
import RecruiterMinLogo from '../svg/recruiter-min-logo.svg'

const userRoles = cookieUtil.get('user_roles');
const uniqueKey = cookieUtil.get('UniqueKey');
const cRole = uniqueKey && userRoles && userRoles.includes('0');

enum EHeaderNavMenuType{
  Home,
  Job,
  Campus,
  None
}

const JobPageRouterList = new Set([
  '/zhaopin/',
  '/city-*/zhaopin',
]);

const CampusPageRouterList = new Set([
  '/campus/',
  '/campus/comp-list/',
  '/campus/recruit-list/',
  '/campus/live-list/',
]);

const HomeActiveRouter = [
  '/', // www首页
  /^\/city-[A-Za-z]+\/$/, // www首页-城市分站
  '/qiuzhi/', // 求职列表页
  /^\/city-[A-Za-z]+\/zhaogongzuo\//, // 求职列表页-城市分站
  '/zhaogongzuo/', // 找工作列表页
  /^\/city-[A-Za-z]+\/qiuzhi\//, // 找工作列表页-城市分站
];

interface IProps{
  city?: string,
  cityName?: string,
  isLogin: boolean,
  visibleCity: boolean,
  userInfo?: IUserInfo,
  quickMenu?: React.ReactNode
}

const Header: React.FC<IProps> = ({ city, cityName, isLogin, visibleCity = true, userInfo, quickMenu }: IProps) => {
  const [currentNavMenu, setCurrentNavMenu] = useState<EHeaderNavMenuType>(EHeaderNavMenuType.None);
  const [downloadAppModalStyle, setDownloadAppModalStyle] = useState({});
  const [_isLogin, setIsLogin] = useState<boolean>(isLogin);
  const [_userInfo, setUserInfo] = useState<IUserInfo>(userInfo);
  const [_visibelCity, setVisibleCity] = useState<boolean>(visibleCity);
  const { jobURL, getJobURL } = useSearchJob();
  const [zhaoPinURL, setZhaoPinURL] = useState<string>(jobURL);
  const [graySwitch, setGraySwitch] = useState<boolean>(false);

  let { location } = document;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    location = useLocation();
  } catch {
    // console.log('非前端路由');
  }
  const getAccountGray = () => {
    ajax({
      url: `${wwwRoot}/passport/get-caccount-gray.json`,
    }).then(({ data }) => {
      setGraySwitch(data.gray);
    }).catch((error) => {
      console.log(error);
    });
  };

  useEffect(() => {
    _isLogin && getAccountGray();
  }, [_isLogin]);
  
  // 匹配路由
  useEffect(() => {
    let pathName = location?.pathname;
    const lastWord = location?.pathname.slice(-1);
    if (lastWord !== '/') {
      pathName = `${pathName}/`;
    }

    const homeActive = HomeActiveRouter.some((e) => (e instanceof RegExp ? e.test(pathName) : e === pathName));

    // 设置高亮菜单项
    if (homeActive) {
      setCurrentNavMenu(EHeaderNavMenuType.Home);
    } else if (JobPageRouterList.has(pathName) || isCityZhaoPin) {
      setCurrentNavMenu(EHeaderNavMenuType.Job);
    } else if (CampusPageRouterList.has(pathName)) {
      setCurrentNavMenu(EHeaderNavMenuType.Campus);
    }

    // 显示城市
    if (isWWW) {
      if (homeActive) {
        setVisibleCity(true);
      } else if (isCityZhaoPin) {
        setVisibleCity(false);
      } else {
        setVisibleCity(isCityRouter);
      }
    } else {
      setVisibleCity(false);
    }
  }, [location?.pathname]);

  // 初始化
  useEffect(() => {
    setUserInfo(userInfo);
    setIsLogin(isLogin || userUtil.isLogin);
  }, [isLogin, userInfo]);

  useEffect(() => {
    setZhaoPinURL(getJobURL());
  }, [_userInfo]);

  // 监听登录成功
  useEffect(() => {
    const loginSuccess = () => {
      // setIsLogin(true);
      // 强制刷新页面，避免各种数据问题
      window.location.reload();
    };
    eventBus.addListener(LOGIN_SUCCESS, loginSuccess);

    return () => {
      eventBus.removeListener(LOGIN_SUCCESS, loginSuccess);
    };
  }, []);

  // 登录后如果没有用户信息，获取用户信息(主要用于未使用framework的seo页面)
  useEffect(() => {
    if (_isLogin && !_userInfo) {
      loadUserInfoFn(true).then(({ data }) => {
        setUserInfo(data);
      }).catch(({ message }) => {
        message && Message.info(message);
      });
    }
  }, [_isLogin, _userInfo]);

  const handleDownloadAppMouseOver = useCallback(() => {
    const el = document.querySelector('#header-nav-menu-download-app');
    const { top, left } = el.getBoundingClientRect();
    const { offsetHeight, offsetWidth } = el;
    const leftDis = left - 378 + offsetWidth + 15;
    const topDis = offsetHeight + top - 5;
    setDownloadAppModalStyle({
      top: topDis,
      left: leftDis,
      display: 'block',
    });
  }, []);

  const handleDownloadAppMouseLeave = useCallback(() => {
    setDownloadAppModalStyle({
      display: 'none',
    });
  }, []);

  const getUserName = useCallback(() => {
    const name = _userInfo?.userShowName || '';
    if (name.length > 4) {
      return `${name.slice(0, 4)}...`;
    }
    return name;
  }, [_userInfo]);

  const handleClickedLogin = useCallback(() => {
    requireLoginModal({});
  }, []);

  return (
    <>
      <header id="framework-pc-header-container">
        <nav className="header-content-box">
          <a id="header-logo-box" href={wwwRoot} title="猎聘网">
            <img src="https://download.lietou-static.com/img/610bb5592a02d60fe2b896b202u.png" title="猎聘网" alt="" />
          </a>

          {
            _visibelCity && (
              <a id="header-city-box" href={`${wwwRoot}/citylist/`} title="城市切换">
                <div className="header-city-box">
                  <CityAddressSVG />
                  <span>{cityName || '全国'}</span>
                  <CityArrowSVG />
                </div>
              </a>
            )
          }

          <ul className="header-nav-menu">
            <li
              className={
                classnames(
                  'header-menu-item',
                  'header-nav-menu-item',
                  currentNavMenu === EHeaderNavMenuType.Home ? 'active-menu-item' : '',
                )
              }
            >
              <a id="header-nav-menu-home" href={city ? `${wwwRoot}/city-${city}/` : wwwRoot}>首页</a>
            </li>
            <li
              className={
                classnames(
                  'header-menu-item',
                  'header-nav-menu-item',
                  currentNavMenu === EHeaderNavMenuType.Job ? 'active-menu-item' : '',
                )
              }
            >
              <a
                id="header-nav-menu-job"
                href={zhaoPinURL}
              >
                职位
              </a>
            </li>
            <li
              className={
                classnames(
                  'header-menu-item',
                  'header-nav-menu-item',
                  currentNavMenu === EHeaderNavMenuType.Campus ? 'active-menu-item' : '',
                )
              }
            >
              <Dropdown
                overlay={CampusSubMenu}
                overlayClassName="header-nav-menu-campus"
                placement="bottomCenter"
                getPopupContainer={() => document.querySelector('#header-nav-menu-campus')}
              >
                <a
                  id="header-nav-menu-campus"
                  href={`${wwwRoot}/campus/`}
                >
                  <span>校园</span>
                </a>
              </Dropdown>
            </li>
            <li className="header-menu-item header-nav-menu-item">
              <Dropdown
                overlay={ResumeOptimizationSubMenu}
                overlayClassName="header-nav-menu-resume-optimization"
                placement="bottomCenter"
                getPopupContainer={() => document.querySelector('#header-nav-menu-resume-optimization')}
              >
                <a
                  id="header-nav-menu-resume-optimization"
                  href={`${vasRoot}/view-intro?utm_content=pcjuhexiala&imscid=R000014437`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span>简历优化</span>
                </a>
              </Dropdown>
            </li>
            <li
              className="header-menu-item header-nav-menu-item download-app-item"
              onMouseOver={handleDownloadAppMouseOver}
              onMouseLeave={handleDownloadAppMouseLeave}
            >
              <span id="header-nav-menu-download-app">
                猎聘APP
              </span>

              <div
                id="fix-header-download-app-modal"
                style={downloadAppModalStyle}
              >
                <img src={isCn ? 'https://download.lietou-static.com/img/61415712253df529f61dddc708u.png' : 'https://download.lietou-static.com/img/614157155d455b005d25211208u.png'} alt="" />
              </div>
            </li>
          </ul>

          <div className="header-quick-menu-box">
            {
              !_isLogin ? (
                <ul className="header-quick-menu-not-login">
                  {/* <li className="header-menu-item header-quick-menu-not-login-item">
                    <span id="header-quick-menu-upload-resume">上传简历</span>
                  </li> */}
                  <li className="header-menu-item header-quick-menu-not-login-item">
                    <a
                      id="header-quick-menu-hunter"
                      target="_blank"
                      href={`${hRoot}/#sfrom=click-pc_homepage-front_navigation-hunter_new`}
                      rel="noreferrer"
                    >
                      我是猎头
                    </a>
                  </li>
                  <li className="header-menu-item header-quick-menu-not-login-item">
                    <a
                      id="header-quick-menu-recruiter"
                      target="_blank"
                      href={`${passportRoot}/logout?rdurl=${lptRoot}/user/login#sfrom=click-pc_homepage-front_navigation-ecomphr_new`}
                      rel="noreferrer"
                    >
                      我是招聘方
                    </a>
                    <RecruiterMinLogo className="recruiter-min-logo"/>
                  </li>
                  <li
                    className="header-menu-item header-quick-menu-not-login-item"
                    onClick={handleClickedLogin}
                  >
                    <span id="header-quick-menu-login">登录/注册</span>
                  </li>
                </ul>
              ) : (
                quickMenu || (
                <ul className="header-quick-menu-login">
                  {/* <li className="header-menu-item header-quick-menu-login-item">
                    <a
                      id="header-quick-menu-login-recruiter"
                      target="_blank"
                      href={`${passportRoot}/logout?rdurl=${lptRoot}/user/login#sfrom=click-pc_homepage-front_navigation-ecomphr_new`}
                    >
                      我要招人
                    </a>
                  </li> */}
                  <li className="header-menu-item header-quick-menu-login-item">
                    {
                      cRole ? (
                        <Dropdown
                          overlay={graySwitch ? QuickMenuSubMenuGray : QuickMenuSubMenu}
                          overlayClassName="header-quick-menu-user-info"
                          placement="bottomCenter"
                          getPopupContainer={() => document.querySelector('#header-quick-menu-user-info')}
                        >
                          <div id="header-quick-menu-user-info">
                            <span>你好，</span>
                            <span className="header-quick-menu-username ellipsis-1">{getUserName()}</span>
                            {
                              _userInfo?.userPhoto && (
                                <img
                                  alt=""
                                  className="header-quick-menu-userPhoto"
                                  src={`//image0.lietou-static.com/minix/${_userInfo?.userPhoto}`}
                                />
                              )
                            }
                          </div>
                        </Dropdown>
                      ) : null
                    }
                  </li>
                </ul>
                )
              )
            }
          </div>
        </nav>
      </header>
    </>
  );
};

export default Header;
